import { Routes } from '@angular/router';

import { blockAgencyManagerGuard, isAddProjectRoute } from '@guards/agency-manager-block.guard';
import { dashboardAccessGuard } from '@guards/dashboard-access.guard';
import { entityUpsertPermissionGuard } from '@guards/entity-permission.guard';
import { privateRouteGuard } from '@guards/private-route.guard';
import { projectContentWriteGuard } from '@guards/project-content-write.guard';

import { ALL_PERMISSIONS } from '@models/permission.model';

export const PROJECT_ROUTES: Routes = [
  {
    path: 'project',
    children: [
      {
        path: '',
        redirectTo: 'add',
        pathMatch: 'full',
      },
      {
        path: ':id',
        loadComponent: () =>
          import('@pages/project-upsert/project-upsert.component').then(
            (c) => c.ProjectUpsertComponent,
          ),
        data: { layout: 'private' },
        /* FEC-1160: revoking Create/Edit on Settings › Permissions has to
           actually close this screen — `/project/add` needs CREATE_PROJECT,
           `/project/:id` needs UPDATE_PROJECT. */
        canActivate: [
          // FEC-1256: Agency Managers get no Add Project, whatever the catalogue grants.
          blockAgencyManagerGuard(isAddProjectRoute),
          entityUpsertPermissionGuard(
            ALL_PERMISSIONS.CREATE_PROJECT,
            ALL_PERMISSIONS.UPDATE_PROJECT,
          ),
        ],
      },
    ],
    data: { layout: 'private' },
    canActivate: [privateRouteGuard],
  },
  {
    path: 'precinct',
    children: [
      {
        path: ':id',
        loadComponent: () =>
          import('@pages/project-upsert/project-upsert.component').then(
            (c) => c.ProjectUpsertComponent,
          ),
        data: { layout: 'private' },
        // Precincts live under the same Projects & Precincts capability row.
        canActivate: [
          entityUpsertPermissionGuard(
            ALL_PERMISSIONS.CREATE_PROJECT,
            ALL_PERMISSIONS.UPDATE_PROJECT,
          ),
        ],
      },
    ],
    data: { layout: 'private' },
    canActivate: [privateRouteGuard],
  },
  {
    path: 'project-detail/:id',
    loadComponent: () =>
      import('@pages/project-details/project-details.component').then(
        (c) => c.ProjectDetailsComponent,
      ),
    children: [
      {
        path: 'view',
        loadComponent: () =>
          import('@components/apartment-listing/apartment-listing.component').then(
            (c) => c.ApartmentListingComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'info',
        loadComponent: () =>
          import('@pages/project-details/project-basic-info/project-basic-info.component').then(
            (c) => c.ProjectBasicInfoComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'car-spaces',
        loadComponent: () =>
          import('@components/car-park-storage-listing/car-park-storage-listing.component').then(
            (c) => c.CarParkStorageListingComponent,
          ),
        data: { layout: 'private', extrasListingKind: 'car' },
        // FEC-1252 / FEC-1253 (Hina, 7 Oct): access follows the project permission
        // view_car_park — enforced by CarParkStorageListingComponent, not a role block.
      },
      {
        path: 'announcement',
        loadComponent: () =>
          import('@pages/project-details/matrix-announcement/matrix-announcement.component').then(
            (c) => c.MatrixAnnouncementComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'allocations',
        loadComponent: () =>
          import('@pages/project-details/allocation/allocation.component').then(
            (c) => c.AllocationComponent,
          ),
        data: { layout: 'private' },
      },
      {
        // FEC-1165: the landing page is now a two-tab shell — Applied Upgrades
        // (per-lot, the default) and Upgrade Catalogue (the project-level list
        // this route used to render on its own).
        path: 'upgrades',
        loadComponent: () =>
          import('@pages/project-details/upgrades-tabs/upgrades-tabs.component').then(
            (c) => c.UpgradesTabsComponent,
          ),
        data: { layout: 'private' },
      },
      {
        /* FEC-1180: Collections moved out of the Upgrades screen into their own
         * two-tab page — the collection list, and which lot sits in which. */
        path: 'collections',
        loadComponent: () =>
          import('@pages/project-details/collections-tabs/collections-tabs.component').then(
            (c) => c.CollectionsTabsComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'upgrades/types/add',
        loadComponent: () =>
          import('@pages/project-details/upgrade-type-upsert/upgrade-type-upsert.component').then(
            (c) => c.UpgradeTypeUpsertComponent,
          ),
        data: { layout: 'private' },
        // FEC-1225: add / edit forms are closed to the read-only Developer Viewer.
        canActivate: [projectContentWriteGuard],
      },
      {
        path: 'upgrades/details/add',
        loadComponent: () =>
          import('@pages/project-details/upgrade-upsert/upgrade-upsert.component').then(
            (c) => c.UpgradeUpsertComponent,
          ),
        data: { layout: 'private' },
        // FEC-1225: add / edit forms are closed to the read-only Developer Viewer.
        canActivate: [projectContentWriteGuard],
      },
      {
        path: 'upgrades/details/:id',
        loadComponent: () =>
          import('@pages/project-details/upgrade-upsert/upgrade-upsert.component').then(
            (c) => c.UpgradeUpsertComponent,
          ),
        data: { layout: 'private' },
        // FEC-1225: add / edit forms are closed to the read-only Developer Viewer.
        canActivate: [projectContentWriteGuard],
      },
      {
        path: 'color-schemes/types/add',
        loadComponent: () =>
          import('@pages/color-scheme-upsert/color-scheme-upsert.component').then(
            (c) => c.ColorSchemeUpsertComponent,
          ),
        data: { layout: 'private' },
        // FEC-1225: add / edit forms are closed to the read-only Developer Viewer.
        canActivate: [projectContentWriteGuard],
      },
      {
        path: 'color-schemes',
        loadComponent: () =>
          import('@pages/project-color-schemes/project-color-schemes.component').then(
            (c) => c.ProjectColorSchemesComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'color-scheme/add',
        redirectTo: 'color-schemes/types/add',
        pathMatch: 'full',
      },
      {
        path: 'variations',
        loadComponent: () =>
          import('@pages/project-details/project-variations/project-variations.component').then(
            (c) => c.ProjectVariationsComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'variations/types/add',
        loadComponent: () =>
          import('@pages/project-details/variation-type-upsert/variation-type-upsert.component').then(
            (c) => c.VariationTypeUpsertComponent,
          ),
        data: { layout: 'private' },
        // FEC-1225: add / edit forms are closed to the read-only Developer Viewer.
        canActivate: [projectContentWriteGuard],
      },
      {
        path: 'agencies',
        loadComponent: () =>
          import('@pages/project-details/project-agencies/project-agencies.component').then(
            (c) => c.ProjectAgenciesComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'version-control-history',
        loadComponent: () =>
          import('@pages/project-details/version-control-history-directory/version-control-history-directory.component').then(
            (c) => c.VersionControlHistoryDirectoryComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'storage',
        loadComponent: () =>
          import('@components/car-park-storage-listing/car-park-storage-listing.component').then(
            (c) => c.CarParkStorageListingComponent,
          ),
        data: { layout: 'private', extrasListingKind: 'storage' },
        // FEC-1252 / FEC-1253 (Hina, 7 Oct): access follows the project permission
        // view_car_park — enforced by CarParkStorageListingComponent, not a role block.
      },
      {
        path: 'allocation',
        loadComponent: () =>
          import('@pages/project-details/allocation/allocation.component').then(
            (c) => c.AllocationComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('@pages/project-details/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES,
          ),
        canActivate: [dashboardAccessGuard],
        data: { layout: 'private' },
      },
      // {
      //   path: 'reporting',
      //   loadComponent: () =>
      //     import('@pages/project-details/reporting/reporting.component').then(
      //       (c) => c.ReportingComponent,
      //     ),
      //   data: { layout: 'private' },
      // },
      {
        path: 'documents',
        loadComponent: () =>
          import('@components/library-documents/library-documents.component').then(
            (c) => c.LibraryDocumentsComponent,
          ),
        data: { layout: 'private' },
      },
      /* FEC-1171: opening a project lands on its Dashboard, not the Apartments
       * board. Several entry points link at the bare `/project-detail/:id`
       * (the property card's image and its list-layout title, and the precinct
       * listing's own navigate) rather than naming a section, so they all
       * inherited whatever this redirect pointed at — which is why the QA note
       * about "it opens the apartment page" survived the earlier fix to the
       * Projects dropdown. Routing it here fixes every one of them at once.
       *
       * Roles without dashboard access are not stranded: `dashboardAccessGuard`
       * on the `dashboard` child sends them straight back to `view`. */
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
    // canActivate: [privateRouteGuard],
  },
];
